// API layer for the Catalyst Tracker feature (catalyst_tracker Flask blueprint).
// The backend is a generic Firestore-like docstore: collections are fetched/written through a small
// set of routes under /catalyst-tracker/api. See catalyst_tracker/docstore.py and catalyst_tracker/api.py
// for the server-side contract this mirrors exactly.
import api from './api';
import {
  TrackerCatalyst,
  TrackerChangelogEntry,
  TrackerCollectionResponse,
  TrackerConference,
  TrackerDoc,
  TrackerLandscapeTrial,
  TrackerPipelineCompany,
  TrackerPipelineProgramDetail,
  TrackerRegistryDoc,
  TrackerSearchDoc,
  TrackerSyncMeta,
  TrackerSuggestion,
  TrackerSuggestionReview,
  TrackerWatchDoc,
} from '../types';

const BASE = '/catalyst-tracker/api';

export type TrackerCollection =
  | 'catalysts'
  | 'suggestions'
  | 'changelog'
  | 'meta'
  | 'conferences'
  | 'pipeline'
  | 'pipeline_programs'
  | 'searches'
  | 'watch';

export interface ListOptions {
  limit?: number;
  order?: { field: string; direction: 'asc' | 'desc' };
  since?: string;
}

function orderParam(order?: ListOptions['order']): string | undefined {
  if (!order) return undefined;
  return `${order.field}:${order.direction}`;
}

async function listCollection<T>(collection: TrackerCollection, opts: ListOptions = {}): Promise<TrackerCollectionResponse<T>> {
  const response = await api.get(`${BASE}/c/${collection}`, {
    params: {
      limit: opts.limit,
      order: orderParam(opts.order),
      since: opts.since,
    },
  });
  if (response.status === 304) {
    return { docs: [], version: opts.since || '' };
  }
  return response.data;
}

async function getDocument<T>(collection: TrackerCollection, docId: string): Promise<TrackerDoc<T> | null> {
  const response = await api.get(`${BASE}/d/${collection}/${encodeURIComponent(docId)}`);
  if (!response.data?.exists) return null;
  return { id: docId, version: response.data.version, data: response.data.data };
}

async function setDocument<T extends object>(collection: TrackerCollection, docId: string, data: T, ifVersion?: number) {
  const response = await api.put(`${BASE}/d/${collection}/${encodeURIComponent(docId)}`, data, {
    params: ifVersion != null ? { if_version: ifVersion } : undefined,
  });
  return response.data as { id: string; version: number };
}

async function updateDocument<T extends object>(collection: TrackerCollection, docId: string, data: Partial<T>, ifVersion?: number) {
  const response = await api.patch(`${BASE}/d/${collection}/${encodeURIComponent(docId)}`, data, {
    params: ifVersion != null ? { if_version: ifVersion } : undefined,
  });
  return response.data as { id: string; version: number };
}

async function addDocument<T extends object>(collection: TrackerCollection, data: T) {
  const response = await api.post(`${BASE}/c/${collection}`, data);
  return response.data as { id: string; version: number };
}

async function deleteDocument(collection: TrackerCollection, docId: string, ifVersion?: number) {
  const response = await api.delete(`${BASE}/d/${collection}/${encodeURIComponent(docId)}`, {
    params: ifVersion != null ? { if_version: ifVersion } : undefined,
  });
  return response.data as { id: string; version: number };
}

export const catalystTrackerApi = {
  // ---- reads ----
  async listCatalysts(opts: ListOptions = {}): Promise<TrackerCatalyst[]> {
    const res = await listCollection<TrackerCatalyst>('catalysts', { limit: 1000, ...opts });
    return res.docs.map((d) => ({ ...d.data, id: d.id }));
  },

  // Asset Radar landscape trials for a tumor type, not yet on the calendar (Timeline banner's "All trials").
  async getLandscapeTrials(histology: string): Promise<TrackerLandscapeTrial[]> {
    const response = await api.get(`${BASE}/landscape-trials`, { params: { histology } });
    return response.data?.rows || [];
  },

  async listConferences(): Promise<TrackerConference[]> {
    const res = await listCollection<TrackerConference>('conferences', { limit: 500 });
    return res.docs.map((d) => ({ ...d.data, id: d.id }));
  },

  async listChangelog(): Promise<TrackerChangelogEntry[]> {
    const res = await listCollection<TrackerChangelogEntry>('changelog', {
      limit: 300,
      order: { field: 'at', direction: 'desc' },
    });
    return res.docs.map((d) => ({ ...d.data, id: d.id }));
  },

  async listSuggestions(): Promise<TrackerSuggestion[]> {
    const res = await listCollection<Omit<TrackerSuggestion, 'id'>>('suggestions', { limit: 500 });
    return res.docs.map((d) => ({ ...(d.data as object), id: d.id }) as TrackerSuggestion);
  },

  async listPipeline(): Promise<TrackerPipelineCompany[]> {
    const res = await listCollection<Omit<TrackerPipelineCompany, 'id'>>('pipeline', { limit: 1000 });
    return res.docs.map((d) => ({ ...(d.data as object), id: d.id }) as TrackerPipelineCompany);
  },

  async getPipelineProgram(company: string, asset: string, program: string): Promise<TrackerPipelineProgramDetail | null> {
    const path = `pipeline/${encodeURIComponent(company)}/assets/${encodeURIComponent(asset)}/programs/${encodeURIComponent(program)}`;
    const response = await api.get(`${BASE}/d/${path}`);
    if (!response.data?.exists) return null;
    return { ...(response.data.data as object), id: path } as TrackerPipelineProgramDetail;
  },

  async getWatch(userId: string): Promise<TrackerDoc<TrackerWatchDoc> | null> {
    return getDocument<TrackerWatchDoc>('watch', userId);
  },

  async saveWatch(userId: string, data: TrackerWatchDoc): Promise<{ id: string; version: number }> {
    // Favorites are always a full replace (matches the old client's `watchRef.set(payload)`).
    return setDocument('watch', userId, data);
  },

  // meta/sync (tracker.js:1740's "Last run" stat) — see TrackerSyncMeta's doc comment for why this
  // currently always reads null (no backend writer yet), and degrades gracefully when it does.
  async getSyncMeta(): Promise<TrackerSyncMeta | null> {
    const doc = await getDocument<TrackerSyncMeta>('meta', 'sync');
    return doc?.data || null;
  },

  // meta/registry (catalyst_tracker/registry.py) — CT.gov start/primary-completion/status per NCT, used to
  // draw the Timeline banner's trial bars.
  async getRegistry(): Promise<TrackerRegistryDoc> {
    const doc = await getDocument<TrackerRegistryDoc>('meta', 'registry');
    return doc?.data || {};
  },

  // ---- suggestions workflow ----
  async submitSuggestion(data: Partial<TrackerSuggestion>): Promise<{ id: string; version: number }> {
    return addDocument('suggestions', data);
  },

  async reviewSuggestion(suggestionId: string, decision: TrackerSuggestionReview, note?: string) {
    const response = await api.post(`${BASE}/suggestions/${encodeURIComponent(suggestionId)}/review`, { decision, note });
    return response.data as { id: string; status_review: string; catalyst_id: string };
  },

  // ---- direct catalyst writes (owner only; everyone else goes through suggestions) ----
  async setCatalyst(id: string, data: Partial<TrackerCatalyst>, ifVersion?: number) {
    return setDocument('catalysts', id, data, ifVersion);
  },

  async updateCatalyst(id: string, data: Partial<TrackerCatalyst>, ifVersion?: number) {
    return updateDocument('catalysts', id, data, ifVersion);
  },

  async addCatalyst(data: Partial<TrackerCatalyst>) {
    return addDocument('catalysts', data);
  },

  async deleteCatalyst(id: string, ifVersion?: number) {
    return deleteDocument('catalysts', id, ifVersion);
  },

  // ---- tools ----
  async lookupTrial(nctId: string): Promise<Record<string, unknown>> {
    const response = await api.post(`${BASE}/tool/get_trial_details`, { nct_id: nctId });
    return response.data?.payload;
  },

  // Queues a worker search (editor only; server enforces via roles.is_editor). `text` must contain a
  // "SEARCH_ID: <slug>" line — see catalyst_tracker/worker/jobs.py's enqueue_search()/search_id_of().
  async fireTrigger(text: string): Promise<{ ok: boolean }> {
    const response = await api.post(`${BASE}/tool/fire_trigger`, { text });
    return response.data;
  },

  // ---- searches (worker queue; see catalyst_tracker/worker/jobs.py) ----
  async getSearch(slug: string): Promise<TrackerDoc<TrackerSearchDoc> | null> {
    return getDocument<TrackerSearchDoc>('searches', slug);
  },

  async listSearches(): Promise<TrackerDoc<TrackerSearchDoc>[]> {
    const res = await listCollection<TrackerSearchDoc>('searches', { limit: 500 });
    return res.docs;
  },

  // ---- blob attachments (owner only) ----
  async uploadBlob(file: File): Promise<{ id: string; url: string }> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`${BASE}/blob`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  blobUrl(assetId: string): string {
    return `/catalyst-tracker/_blob/${assetId}`;
  },
};

export default catalystTrackerApi;
